
	import { defineComponent, nextTick, ref } from 'vue';
	import { useCreatingForm } from '@/core/hooks/use-creating-form';
	import { useRouterCustom } from '@/core/hooks/use-router-custom';
	import { resolveListPageForRoute } from '@/core/helpers/list-page-context';
	import {
		IDonViResponseBase,
		IToaXeResponseBase,
	} from '@/core/interfaces/ApiResponses';
	import {
		getErrorMsg,
		lengthValidationRules,
		swalNotification,
	} from '@/core/helpers/utils';
	import {
		setBreadcrumbLinks,
		setCurrentPageTitle,
	} from '@/core/helpers/breadcrumb';
	import { useRoute } from 'vue-router';
	import ToaXeService from '@/core/services/ToaXe.service';
	import { DonViService } from '@/core/services/DonVi.service';
	import DanhMucToaXeService from '@/core/services/DanhMucToaXe.service';
	import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
	import { useButtonCustom } from '@/core/hooks/use-button-custom';
	import { useLoading } from '@/core/hooks/use-loading';
	import PageContentBase from '@/layout/_shared/PageContentBase.vue';
	import DivRow from '@/layout/_shared/DivRow.vue';
	import DivCol from '@/layout/_shared/DivCol.vue';
	import LabelCustom from '@/components/shared/label-custom/LabelCustom.vue';
	import DanhMucToaXeSearchInput from '@/components/shared/danh-muc-toa-xe-search-input/DanhMucToaXeSearchInput.vue';
	import { mapMutations } from 'vuex';
	import useFormInput from '@/core/hooks/use-form-input';
	import Swal from 'sweetalert2';
	import type { ElInput } from 'element-plus';
	import { usePagination } from '@/core/hooks/use-pagination';
	import DanhSachToaXeTable from '@/views/crafted/pages/toa-xe/danh-sach-toa-xe-table/DanhSachToaXeTable.vue';
	import Pagination from '@/components/shared/pagination/Pagination.vue';

	export default defineComponent({
		name: 'toa-xe-form',
		components: {
			ButtonCustom,
			DanhSachToaXeTable,
			DanhMucToaXeSearchInput,
			DivCol,
			DivRow,
			Pagination,
			PageContentBase,
			LabelCustom,
		},
		setup() {
			const route = useRoute();
			const { id, formRef, loading, isCreating } = useCreatingForm();
			const { goBack, replace } = useRouterCustom();
			const { ButtonTypeColors, ButtonsType } = useButtonCustom();
			const { startLoading, endLoading, isLoading } = useLoading();
			const originSoHieuToaXe = ref('');
			const originMaToaXe = ref('');
			const tenToaXeInputRef = ref<InstanceType<typeof ElInput>>();
			const danhMucToaXeSearchInputRef = ref<{
				focus: () => void;
			}>();
			const { focusFirstErrInputV2 } = useFormInput();
			const { currentPage, totalItems, perPage } = usePagination();
			currentPage.value = resolveListPageForRoute(route.query, {
				isCreate: isCreating.value,
			});
			const toaXeItems = ref<IToaXeResponseBase[]>([]);

			const toaXeForm = ref({
				id_dm_toa_xe: 0,
				id_don_vi: 0,
				ten_toa_xe: '',
				ma_toa_xe: '',
				ghi_chu: '',
				so_hieu: '',
			});

			const rules = ref({
				// id_don_vi: [
				// 	{
				// 		type: 'number',
				// 		min: 1,
				// 		message: 'Phải chọn đơn vị quản lý',
				// 	},
				// ],
				id_dm_toa_xe: [
					{
						type: 'number',
						message: 'Phải chọn nhóm toa xe',
						min: 1,
					},
				],
				ghi_chu: [
					...lengthValidationRules({
						isRequired: false,
						max: 160,
						name: 'Ghi chú',
					}),
				],
				ten_toa_xe: [
					...lengthValidationRules({
						name: 'Tên toa xe',
					}),
				],
				ma_toa_xe: [
					{
						required: true,
						message: 'Mời bạn nhập mã toa xe',
						trigger: 'blur',
					},
					{
						trigger: 'blur',
						validator: async function (_, ma_toa_xe, fn) {
							if (!ma_toa_xe) {
								fn();

								return;
							}

							if (!isCreating.value && ma_toa_xe === originMaToaXe.value) {
								fn();

								return;
							}

							try {
								await ToaXeService.validateToaXeData({
									ma_toa_xe,
								});
							} catch (e) {
								fn(new Error(getErrorMsg(e)));

								return;
							}

							fn();
						},
					},
				],
				so_hieu: [
					{
						required: true,
						message: 'Mời bạn nhập số hiệu',
						trigger: 'blur',
					},
					{
						trigger: 'blur',
						validator: async function (_, so_hieu, fn) {
							if (!isCreating.value && so_hieu === originSoHieuToaXe.value) {
								fn();

								return;
							}

							try {
								await ToaXeService.validateToaXeData({
									so_hieu,
								});
							} catch (e) {
								fn(new Error(getErrorMsg(e)));

								return;
							}

							fn();
						},
					},
				],
			});

			return {
				currentPage,
				toaXeForm,
				toaXeId: id,
				formRef,
				loading,
				isCreating,
				goBack,
				replace,
				route,
				ButtonTypeColors,
				ButtonsType,
				rules,
				originMaToaXe,
				originSoHieuToaXe,
				tenToaXeInputRef,
				danhMucToaXeSearchInputRef,
				focusFirstErrInputV2,
				perPage,
				startLoading,
				endLoading,
				toaXeItems,
				totalItems,
				isLoading,
			};
		},

		data() {
			return {
				donViItems: [] as IDonViResponseBase[],
				danhMucToaXeDisplay: '',
				danhMucToaXeInputKey: 0,
				isFormDataReady: false,
			};
		},

		async mounted() {
			setBreadcrumbLinks([
				{
					text: 'Toa xe',
					routeData: {
						name: 'danh-sach-toa-xe',
					},
				},
				...(this.isCreating
					? []
					: [
							{
								text: 'Chi tiết',
								routeData: {
									name: 'chi-tiet-toa-xe',
									params: {
										id: this.toaXeId,
									},
								},
							},
						]),
				{
					text: this.isCreating ? 'Tạo mới' : 'Chỉnh sửa',
				},
			]);

			await this.startLoading();

			try {
				const {
					data: { data: donViResponse },
				} = await DonViService.fetchDepartmentsByCapDonVi(2);

				this.donViItems = donViResponse;
				await this.loadToaXeDetail();
				this.isFormDataReady = true;
				await this.$nextTick();
			} catch (err) {
				await swalNotification(
					'Xin lỗi, có vẻ có lỗi xảy ra khi lấy thông tin',
					'error',
				);
			} finally {
				await this.endLoading();
			}

			await this.fetchData();
			await this.focusDanhMucToaXeInput();
		},

		watch: {
			async currentPage() {
				await this.fetchData();
			},
			async perPage() {
				if (this.currentPage === 1) {
					await this.fetchData();

					return;
				}

				this.currentPage = 1;
			},
			/** Reload form when table row actions navigate create ↔ edit or another edit id */
			toaXeId: {
				async handler(nextId: number, prevId: number) {
					if (this.isCreating) {
						if (prevId > 0) {
							await this.resetToCreateMode();
						}

						return;
					}

					if (!nextId || nextId === prevId) {
						return;
					}

					this.currentPage = resolveListPageForRoute(this.route.query, {
						isCreate: false,
					});

					await this.startLoading();

					try {
						this.isFormDataReady = false;
						await this.loadToaXeDetail();
						this.isFormDataReady = true;
						await this.$nextTick();
						await this.focusDanhMucToaXeInput();
						window.scrollTo({ top: 0, behavior: 'smooth' });
					} finally {
						await this.endLoading();
					}
				},
			},
			async isCreating(creating: boolean, wasCreating: boolean) {
				if (creating && !wasCreating) {
					await this.resetToCreateMode();
				}
			},
		},

		methods: {
			...mapMutations('ConfirmationDialogModule', {
				setDialog: 'setDialog',
				closeDialog: 'closeDialog',
			}),

			async loadToaXeDetail() {
				if (this.isCreating || !this.toaXeId) {
					document.title = 'Tạo mới - Toa xe - Đường sắt Hà Nội';
					setCurrentPageTitle('Tạo mới toa xe');

					return;
				}

				const {
					data: { data: res },
				} = await ToaXeService.get(this.toaXeId);

				const dmToaXeId = res.danh_muc_toa_xe[0]?.id;

				if (dmToaXeId) {
					const {
						data: { data: dmData },
					} = await DanhMucToaXeService.get(dmToaXeId);

					this.danhMucToaXeDisplay = `${dmData.ten_danh_muc} - (${dmData.ma_danh_muc})`;
				} else {
					this.danhMucToaXeDisplay = '';
				}

				document.title = `${res.ten_toa_xe} (${res.so_hieu}) - Chỉnh sửa - Toa xe - Đường sắt Hà Nội`;
				this.toaXeForm.ma_toa_xe = res.ma_toa_xe;
				this.toaXeForm.ten_toa_xe = res.ten_toa_xe;
				this.toaXeForm.ghi_chu = res.ghi_chu;
				this.toaXeForm.so_hieu = res.so_hieu;
				this.toaXeForm.id_don_vi = res.id_don_vi;
				this.toaXeForm.id_dm_toa_xe = dmToaXeId || 0;
				this.originMaToaXe = res.ma_toa_xe;
				this.originSoHieuToaXe = res.so_hieu;
				this.danhMucToaXeInputKey += 1;

				setCurrentPageTitle(
					`${this.toaXeForm.ten_toa_xe} (${this.toaXeForm.so_hieu})`,
				);
			},

			async resetToCreateMode() {
				await this.clear(true, true);
				this.originMaToaXe = '';
				this.originSoHieuToaXe = '';
				this.danhMucToaXeInputKey += 1;
				this.currentPage = resolveListPageForRoute(this.route.query, {
					isCreate: true,
				});
				document.title = 'Tạo mới - Toa xe - Đường sắt Hà Nội';
				setCurrentPageTitle('Tạo mới toa xe');
				this.isFormDataReady = true;
				await this.$nextTick();
				await this.focusDanhMucToaXeInput();
				window.scrollTo({ top: 0, behavior: 'smooth' });
			},

			async fetchData() {
				try {
					this.startLoading();

					const {
						data: {
							data: { data: res, total },
						},
					} = await ToaXeService.list(this.currentPage, this.perPage);

					this.toaXeItems = res;
					this.totalItems = total;
					this.endLoading();
				} catch {
					this.endLoading();

					await swalNotification(
						'Có lỗi xảy ra khi lấy danh sách toa xe',
						'error',
					);
				}
			},

			async refreshFirstPage() {
				if (this.currentPage !== 1) {
					this.currentPage = 1;

					return;
				}

				await this.fetchData();
			},

			async clear(resetDonVi = true, resetDanhMucToaXe = true) {
				this.toaXeForm.ten_toa_xe = '';
				this.toaXeForm.so_hieu = '';
				this.toaXeForm.ghi_chu = '';

				if (resetDanhMucToaXe) {
					this.toaXeForm.id_dm_toa_xe = 0;
					this.danhMucToaXeDisplay = '';
				}

				if (resetDonVi) {
					this.toaXeForm.id_don_vi = 0;
				}

				if (this.isCreating) {
					this.toaXeForm.ma_toa_xe = '';
				}

				await nextTick();
				this.formRef?.clearValidate();
			},

			async clearForContinue() {
				await this.clear(false, false);
				this.danhMucToaXeInputKey += 1;
				await this.$nextTick();
				this.formRef?.clearValidate();
			},

			async focusDanhMucToaXeInput() {
				await this.$nextTick();
				this.danhMucToaXeSearchInputRef?.focus();
			},

			async focusTenToaXeInput() {
				if (document.activeElement instanceof HTMLElement) {
					document.activeElement.blur();
				}

				await this.$nextTick();

				this.tenToaXeInputRef?.focus();
				this.tenToaXeInputRef?.select();
			},

			async notifyAfterContinue(
				message: string,
				type: 'error' | 'success' = 'success',
			) {
				await Swal.fire({
					text: message,
					icon: type,
					buttonsStyling: false,
					confirmButtonText: 'Đã hiểu',
					focusConfirm: false,
					customClass: {
						confirmButton: `btn btn-${type === 'error' ? 'danger' : 'primary'}`,
					},
				});

				await this.focusTenToaXeInput();
			},

			onClearDanhMucToaXe() {
				this.toaXeForm.id_dm_toa_xe = 0;
				this.danhMucToaXeDisplay = '';
			},

			onSelectDanhMucToaXe({
				id,
				ten_danh_muc,
				ma_danh_muc,
			}: {
				id: number;
				ten_danh_muc: string;
				ma_danh_muc?: string;
			}) {
				this.toaXeForm.id_dm_toa_xe = id;
				this.danhMucToaXeDisplay = ma_danh_muc
					? `${ten_danh_muc} - (${ma_danh_muc})`
					: ten_danh_muc;
			},

			async submit(shouldContinue = false) {
				if (!this.formRef) {
					return;
				}

				this.formRef.validate(async (valid) => {
					if (valid) {
						this.setDialog({
							title: `${
								this.isCreating
									? 'Tạo mới toa xe'
									: `Toa xe ${this.toaXeForm.ten_toa_xe} (${this.toaXeForm.so_hieu})`
							}`,
							message: this.isCreating
								? shouldContinue
									? 'Tạo mới toa xe với những thông tin này và tiếp tục tạo mới không?'
									: 'Bạn có chắc chắn muốn tạo mới toa xe này với các thông tin đã nhập?'
								: 'Bạn có chắc chắn muốn chỉnh sửa thông tin toa xe này?',
							onConfirm: async () => {
								this.closeDialog();
								await this.$nextTick();

								await this.startLoading(
									this.isCreating ? 'Đang tạo toa xe...' : 'Đang lưu...',
								);

								try {
									if (this.isCreating) {
										const {
											data: {
												data: { id, ma_toa_xe },
											},
										} = await ToaXeService.create({
											ten_toa_xe: this.toaXeForm.ten_toa_xe,
											ma_toa_xe: this.toaXeForm.ma_toa_xe,
											so_hieu: this.toaXeForm.so_hieu,
											id_dm_toa_xe: this.toaXeForm.id_dm_toa_xe,
											id_don_vi: this.toaXeForm.id_don_vi,
											...(this.toaXeForm.ghi_chu && {
												ghi_chu: this.toaXeForm.ghi_chu,
											}),
										});

										await this.endLoading();

										if (shouldContinue) {
											await this.clearForContinue();
											await this.refreshFirstPage();
											await this.notifyAfterContinue(
												'Tạo mới toa xe thành công',
												'success',
											);

											return;
										}

										await swalNotification(
											'Tạo mới toa xe thành công',
											'success',
										);

										this.toaXeId = id;
										this.toaXeForm.ma_toa_xe = ma_toa_xe;
									} else {
										await ToaXeService.update({
											id: this.toaXeId,
											id_dm_toa_xe: this.toaXeForm.id_dm_toa_xe,
											so_hieu: this.toaXeForm.so_hieu,
											ma_toa_xe: this.toaXeForm.ma_toa_xe,
											ten_toa_xe: this.toaXeForm.ten_toa_xe,
											id_don_vi: this.toaXeForm.id_don_vi,
											...(this.toaXeForm.ghi_chu && {
												ghi_chu: this.toaXeForm.ghi_chu,
											}),
										});

										await this.endLoading();

										await swalNotification(
											'Chỉnh sửa toa xe thành công',
											'success',
										);

										if (shouldContinue) {
											await this.refreshFirstPage();

											return;
										}
									}

									await this.replace('/quan-ly/toa-xe/danh-sach');
								} catch (e) {
									await this.endLoading();

									if (shouldContinue) {
										await this.notifyAfterContinue(
											getErrorMsg(e, 'Xin lỗi, có vẻ có lỗi xảy ra'),
											'error',
										);

										return;
									}

									await swalNotification(
										getErrorMsg(e, 'Xin lỗi, có vẻ có lỗi xảy ra'),
										'error',
									);
								}
							},
						});
					} else {
						await this.focusFirstErrInputV2();

						return false;
					}
				});
			},
		},
	});
